import React, { useState, useEffect } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { formsAPI } from "../services/api";

const ErrorReport = () => {
   const { formId } = useParams();
   const [searchParams] = useSearchParams();
   const navigate = useNavigate();

   const formType = searchParams.get("form_type") || "Unknown";

   const [selectedFields, setSelectedFields] = useState([]);
   const [description, setDescription] = useState("");
   const [isSubmitting, setIsSubmitting] = useState(false);
   const [error, setError] = useState("");
   const [formData, setFormData] = useState(null);
   const [screenshot, setScreenshot] = useState(null);
   const [screenshotPreview, setScreenshotPreview] = useState(null);

   useEffect(() => {
      loadFormData();
   }, [formId]);

   const loadFormData = async () => {
      try {
         // Get form data
         const formResponse = await formsAPI.getFormById(formId);
         if (formResponse.success) {
            setFormData(formResponse.data);
         }
      } catch (err) {
         console.error("Error loading form data:", err);
         setError("Failed to load form data");
      }
   };

   // Handle screenshot upload
   const handleScreenshotChange = (e) => {
      const file = e.target.files[0];
      if (file) {
         if (file.size > 5 * 1024 * 1024) {
            // 5MB limit
            setError("Screenshot must be less than 5MB");
            return;
         }

         setScreenshot(file);

         // Create preview
         const reader = new FileReader();
         reader.onloadend = () => {
            setScreenshotPreview(reader.result);
         };
         reader.readAsDataURL(file);
      }
   };

   // Remove screenshot
   const handleRemoveScreenshot = () => {
      setScreenshot(null);
      setScreenshotPreview(null);
   };

   // Define fields based on form type
   const getFormFields = () => {
      if (formType === "dispatch") {
         return [
            "Dispatcher First Name",
            "Dispatcher Last Name",
            "After Hours Phone",
            "Driver First Name",
            "Driver Last Name",
            "Driver Phone Number",
            "Driver Type",
            "Driver Consent",
            "Equipment Type",
            "Truck Number",
            "Trailer Number",
            "Truck VIN",
            "Truck License Plate",
            "Driver Location",
            "Empty Status",
            "Empty Time",
            "Empty Location",
            "Additional Drivers",
         ];
      } else if (formType === "driver_verification") {
         return [
            "Driver First Name",
            "Driver Last Name",
            "Driver Email",
            "Driver Phone",
            "Location",
            "Empty Status",
            "Empty Time",
            "Empty Location",
            "SMS Consent",
            "Truck Photo",
            "Cab Card",
            "CDL Front",
            "CDL Back",
         ];
      }
      return [];
   };

   const handleFieldToggle = (field) => {
      setSelectedFields((prev) => {
         if (prev.includes(field)) {
            return prev.filter((f) => f !== field);
         } else {
            return [...prev, field];
         }
      });
   };

   const handleSubmit = async (e) => {
      e.preventDefault();

      if (selectedFields.length === 0) {
         setError("Please select at least one field with an error");
         return;
      }

      setIsSubmitting(true);
      setError("");

      try {
         // Create form data for multipart submission
         const formData = new FormData();

         // Add error fields as an array
         selectedFields.forEach((field) => {
            formData.append("error_fields[]", field);
         });

         // Add error description
         formData.append("error_description", description);

         // Add screenshot if exists
         if (screenshot) {
            formData.append("screenshot", screenshot);
         }

         console.log("Submitting error report with fields:", selectedFields);
         console.log("Description:", description);
         console.log("Screenshot:", screenshot);

         const response = await formsAPI.submitErrorReport(formId, formData);

         if (response.success) {
            navigate(`/forms/${formId}/errors`);
         } else {
            setError(response.error || "Failed to submit error report");
         }
      } catch (error) {
         console.error("Error submitting report:", error);
         setError("Failed to submit error report");
      } finally {
         setIsSubmitting(false);
      }
   };

   const fields = getFormFields();

   return (
      <div className="form-container">
         <div className="form-header">
            <h1>Report Form Error</h1>
            <p>Help us improve by reporting any errors in the submitted form</p>
         </div>

         <form onSubmit={handleSubmit} className="form-content">
            {error && <div className="alert alert-danger">{error}</div>}

            {/* Form Information */}
            <div className="form-section">
               <div
                  style={{
                     background: "var(--info-bg)",
                     border: "1px solid var(--info-border)",
                     borderRadius: "var(--radius)",
                     padding: "16px",
                     marginBottom: "24px",
                     display: "flex",
                     gap: "16px",
                     alignItems: "flex-start",
                  }}
               >
                  <div
                     style={{
                        fontSize: "24px",
                     }}
                  >
                     ℹ️
                  </div>
                  <div
                     style={{
                        flex: 1,
                        lineHeight: 1.6,
                     }}
                  >
                     <strong>Form ID:</strong> {formId}
                     <br />
                     <strong>Form Type:</strong>{" "}
                     {formType.replace("_", " ").replace(/\b\w/g, (l) => l.toUpperCase())}
                     <br />
                     {formData?.conversation_id && (
                        <>
                           <strong>Conversation ID:</strong> {formData.conversation_id}
                           <br />
                        </>
                     )}
                     {formData?.created_at && (
                        <>
                           <strong>Submitted:</strong>{" "}
                           {new Date(formData.created_at).toLocaleString()}
                        </>
                     )}
                  </div>
               </div>
            </div>

            {/* Field Selection */}
            <div className="form-section">
               <h3 className="section-title">Select Fields with Errors</h3>
               <p className="text-muted mb-4">
                  Check all fields that contain incorrect or problematic information:
               </p>

               <div
                  style={{
                     display: "grid",
                     gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
                     gap: "12px",
                     marginBottom: "24px",
                  }}
               >
                  {fields.map((field) => (
                     <div
                        key={field}
                        style={{
                           background: "var(--background)",
                           padding: "8px 12px",
                           borderRadius: "var(--radius-sm)",
                        }}
                     >
                        <div className="checkbox-wrapper">
                           <input
                              type="checkbox"
                              id={`field-${field}`}
                              checked={selectedFields.includes(field)}
                              onChange={() => handleFieldToggle(field)}
                           />
                           <label htmlFor={`field-${field}`}>
                              {field}
                              {formData?.form_data?.[field] && (
                                 <span
                                    style={{
                                       fontSize: "0.9em",
                                       color: "var(--text-muted)",
                                       marginLeft: "4px",
                                    }}
                                 >
                                    {" "}
                                    = {formData.form_data[field]}
                                 </span>
                              )}
                           </label>
                        </div>
                     </div>
                  ))}
               </div>
            </div>

            {/* Error Description */}
            <div className="form-section">
               <h3 className="section-title">Error Description</h3>
               <div className="form-group">
                  <label htmlFor="description">
                     Please describe what is incorrect about the selected fields
                     <span
                        style={{
                           color: "var(--danger-color)",
                           marginLeft: "4px",
                        }}
                     >
                        *
                     </span>
                  </label>
                  <textarea
                     id="description"
                     value={description}
                     onChange={(e) => setDescription(e.target.value)}
                     rows={4}
                     placeholder="Example: Driver's phone number is incorrect, should be (555) 123-4567"
                     className="form-control"
                     required
                  />
               </div>
            </div>

            {/* Screenshot Upload */}
            <div className="form-section">
               <h3 className="section-title">Screenshot (Optional)</h3>
               <p className="text-muted mb-4">Upload a screenshot showing the error (max 5MB)</p>

               <div
                  style={{
                     marginTop: "12px",
                  }}
               >
                  <input
                     type="file"
                     id="screenshot"
                     accept="image/*"
                     onChange={handleScreenshotChange}
                     style={{
                        display: "none",
                     }}
                  />
                  <label
                     htmlFor="screenshot"
                     style={{
                        display: "inline-block",
                        padding: "8px 16px",
                        background: "var(--primary-color)",
                        color: "white",
                        borderRadius: "var(--radius-sm)",
                        cursor: "pointer",
                        transition: "background 0.2s",
                     }}
                     onMouseOver={(e) => {
                        e.target.style.background = "var(--primary-color-dark)";
                     }}
                     onMouseOut={(e) => {
                        e.target.style.background = "var(--primary-color)";
                     }}
                  >
                     {screenshotPreview ? "Change Screenshot" : "Upload Screenshot"}
                  </label>

                  {screenshotPreview && (
                     <div
                        style={{
                           marginTop: "16px",
                           position: "relative",
                           display: "inline-block",
                        }}
                     >
                        <img
                           src={screenshotPreview}
                           alt="Error Screenshot"
                           style={{
                              maxWidth: "300px",
                              maxHeight: "200px",
                              borderRadius: "var(--radius)",
                              border: "1px solid var(--border-color)",
                           }}
                        />
                        <button
                           type="button"
                           onClick={handleRemoveScreenshot}
                           style={{
                              position: "absolute",
                              top: "8px",
                              right: "8px",
                              background: "rgba(255, 255, 255, 0.9)",
                              border: "none",
                              padding: "4px 8px",
                              borderRadius: "var(--radius-sm)",
                              cursor: "pointer",
                              color: "var(--danger-color)",
                              fontSize: "0.9em",
                           }}
                           onMouseOver={(e) => {
                              e.target.style.background = "white";
                           }}
                           onMouseOut={(e) => {
                              e.target.style.background = "rgba(255, 255, 255, 0.9)";
                           }}
                        >
                           Remove
                        </button>
                     </div>
                  )}
               </div>
            </div>

            {/* Submit Button */}
            <div className="form-section" style={{ paddingTop: 0 }}>
               <button
                  type="submit"
                  className="submit-button"
                  disabled={isSubmitting || selectedFields.length === 0}
               >
                  {isSubmitting ? (
                     <>
                        <span className="loading"></span>
                        Submitting...
                     </>
                  ) : (
                     "Submit Error Report"
                  )}
               </button>
            </div>
         </form>
      </div>
   );
};

export default ErrorReport;
